// src/utils/fontManifest.js
//
// Single source of truth for how a font_key renders, everywhere on the
// web side. Mirrors kFontAssets in the Flutter app (font_manifest.dart).
//
// Replaces four conventions that had grown up independently:
//   canvasRenderer  used font_key directly as a CSS family name
//   font-loader.js  derived a family from font_weight, ignoring font_key
//   pdfBuilder      loaded public/fonts/{companyId}/{font_key}.ttf
//   zplBuilder      ignored fonts entirely and always emitted ^A0
//
// ── FAMILY vs STYLE ───────────────────────────────────────────────
// A manifest entry is a FAMILY — one row in the designer's font
// dropdown. Italic is NOT a family: the designer already has a "Kursiv"
// checkbox writing el.fontItalic, so offering "Inter Italic" as a
// separate dropdown entry would give the user two unrelated ways to ask
// for the same thing, with nothing to reconcile them.
//
// The printer disagrees: ZPL has no notion of style, so italic is a
// separate uploaded file with its own ^CW letter. That mismatch is what
// `variants` is for — the user picks a family and ticks Kursiv, and
// variantFor() resolves the pair to the right file and letter.
//
// PPRightGrotesk models weight the other way round: its three weights
// are three customer_fonts rows and three dropdown entries, so they are
// three families here. Inconsistent with Inter, but it matches what is
// already in the database — do not "fix" it without a migration.
//
// ── WHERE THE FILES COME FROM ─────────────────────────────────────
// cssFamily is what canvasRenderer writes into ctx.font, so it must
// match a family the browser actually has. Inter is loaded from Google
// Fonts in plugins/webfontloader.js — that is why there is no
// @font-face anywhere. PPRightGrotesk is licensed and self-hosted.
//
// Never put a comma in cssFamily: comma is the family separator in CSS,
// so "Inter-VariableFont_opsz,wght" parses as two families, neither of
// which exists, and the browser falls back silently.
//
// The .ttf files are still needed even though the browser gets Inter
// from Google: pdfBuilder runs server-side under pdfkit and cannot
// reach Google Fonts, and the jsPDF path embeds the bytes.
//
// ── WHERE THE .ttf FILES LIVE ─────────────────────────────────────
// `file` is a URL path, so the files sit in each project's public/
// folder and are served from /fonts/. Both projects need their own
// copy — the Vue app serves them to the browser, the Next.js API reads
// them off disk for server-side PDF:
//
//   <vue-app>/public/fonts/<name>.ttf     ← fetched by font-loader.js
//   <next-api>/public/fonts/<name>.ttf    ← read by pdfBuilder
//
// Base names match the Brother .bin uploads (Inter-variable.bin →
// Inter-variable.ttf) so one name covers a font everywhere.
//
// ⚠ pdfBuilder._ttfPath currently builds
// public/fonts/{companyId}/{font_key}.ttf instead. That per-company
// folder cannot hold Inter, which belongs to no company, and it buys
// nothing: font-loader.js already serves the licensed PPRightGrotesk
// files from the shared root to any browser that asks. Access control
// belongs in availableFonts(), which filters the dropdown by
// customer_fonts rows — not in the directory layout. Point _ttfPath at
// the shared root and the two halves agree.

export const FONT_MANIFEST = {
  // ─── Built-in default. No customer_fonts row, no licence. ─────────
  inter: {
    label:     'Inter',
    cssFamily: 'Inter',
    builtIn:   true,
    variants: {
      regular: {
        file:        '/fonts/Inter-variable.ttf',
        zplId:       'I',
        printerFile: 'E:InterVar.ttf',
      },
      italic: {
        file:        '/fonts/Inter-italic.ttf',
        zplId:       'J',
        printerFile: 'E:InterItalic.ttf',
      },
    },
  },

  // ─── Licensed customer fonts. Offered only to companies with a
  //     matching customer_fonts row — never list these unconditionally.
  //     No italic files exist for these: ticking Kursiv gives a slanted
  //     canvas and PDF but an upright print, since ZPL cannot synthesise
  //     a style. Known divergence, not an oversight.
  'PPRightGrotesk-CompactBlack': {
    label:     'PP Right Grotesk Black',
    cssFamily: 'PPRightGroteskBlack',
    builtIn:   false,
    variants: {
      regular: {
        file:        '/fonts/PPRightGrotesk-CompactBlack.ttf',
        zplId:       'Z',
        printerFile: 'E:GrotBlck.otf',
      },
    },
  },
  'PPRightGrotesk-CompactDark': {
    label:     'PP Right Grotesk Dark',
    cssFamily: 'PPRightGroteskDark',
    builtIn:   false,
    variants: {
      regular: {
        file:        '/fonts/PPRightGrotesk-CompactDark.ttf',
        zplId:       'Y',
        printerFile: 'E:GrotDark.otf',
      },
    },
  },
  'PPRightGrotesk-Medium': {
    label:     'PP Right Grotesk Medium',
    cssFamily: 'PPRightGroteskMedium',
    builtIn:   false,
    variants: {
      regular: {
        file:        '/fonts/PPRightGrotesk-Medium.ttf',
        zplId:       'X',
        printerFile: 'E:GrotMed.otf',
      },
    },
  },
}

// The built-in family, used only by companies with no customer_fonts
// rows of their own. NOT the universal default — see defaultFontKeyFor.
export const DEFAULT_FONT_KEY = 'inter'

/// The family an element with no font_key of its own renders in.
///
/// Per-company, not a constant. A company with its own fonts has only
/// those bound on its printers (Z/Y/X for the PPRightGrotesk
/// customers), so defaulting such an element to Inter would emit a ^CW
/// letter nothing on that printer answers to — and ZPL falls back to
/// the built-in bitmap font without complaint.
///
/// Lowest sort_order wins: that column exists to say which of a
/// company's fonts is its primary one. Mirrors
/// FontService.defaultFontKey on the Flutter side.
export function defaultFontKeyFor(customerFontRows = []) {
  const known = customerFontRows.filter(r => FONT_MANIFEST[r.font_key])
  if (known.length === 0) return DEFAULT_FONT_KEY

  const sorted = [...known].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
  )
  return sorted[0].font_key
}

export function fontEntry(fontKey) {
  return FONT_MANIFEST[fontKey] || FONT_MANIFEST[DEFAULT_FONT_KEY]
}

/// Resolves (family, italic) to a concrete file / ^CW letter.
/// Families with no italic file fall back to regular.
export function variantFor(fontKey, italic = false) {
  const entry = fontEntry(fontKey)
  if (italic && entry.variants.italic) return entry.variants.italic
  return entry.variants.regular
}

/// CSS family for canvas rendering. Italic is applied separately via
/// ctx.font's style slot, so this takes no italic argument.
export function cssFamilyFor(fontKey) {
  return fontEntry(fontKey).cssFamily
}

/// ^CW letter for an element, honouring its fontItalic flag.
export function zplIdFor(fontKey, italic = false) {
  return variantFor(fontKey, italic).zplId
}

/// The ^CW binding lines a label needs. Takes elements, not bare keys,
/// so an italic element binds its own file. Emit once per label, before
/// any ^A. De-duplicated by letter.
export function zplFontBindings(elements = []) {
  const seen = new Set()
  const out  = []
  for (const el of elements) {
    const v = variantFor(el.font_key, !!el.fontItalic)
    if (seen.has(v.zplId)) continue
    seen.add(v.zplId)
    out.push(`^CW${v.zplId},${v.printerFile}`)
  }
  return out
}

/// Fonts a company may pick from: the built-ins, plus whatever
/// customer_fonts rows the API returned. Licensed families are never
/// offered to a company with no row for them.
export function availableFonts(customerFontRows = []) {
  const builtIns = Object.entries(FONT_MANIFEST)
    .filter(([, v]) => v.builtIn)
    .map(([key, v]) => ({ font_key: key, font_label: v.label, builtIn: true }))

  const customer = customerFontRows
    .filter(row => FONT_MANIFEST[row.font_key])
    .map(row => ({
      font_key:   row.font_key,
      font_label: row.font_label || FONT_MANIFEST[row.font_key].label,
      builtIn:    false,
    }))

  return [...builtIns, ...customer]
}